import React from "react";
import { Link } from "react-router-dom";
import { Anchor, BookOpen, CloudSun } from "lucide-react";
import { getLocalizedPath } from "@/lib/i18nRoutes";

const rtlLangs = new Set(["he", "ar"]);

const copyByLang = {
  en: {
    updated: "Updated",
    title: "Related sailing guides and planning clusters",
    intro: "Use these connected pages to compare conditions, plan safer routes and continue from broad searches into more specific sailing answers.",
    schema: "Marstick SEO topic clusters",
    clusters: [
      ["Weather topic cluster", "Push sailing-weather queries by connecting wind, waves, Beaufort and tide guides.", ["Wind guide", "Sea state and waves", "Beaufort guide", "Tide guide"]],
      ["Marina topic cluster", "Support marina discovery intent with finder, near-me and country pages.", ["Marina finder", "Marinas near me", "Greece marinas", "Croatia marinas"]],
      ["Learning topic cluster", "Strengthen safety, navigation and passage-planning learning paths.", ["Learning center", "Safety checklist", "Navigation guide", "Passage planning"]],
    ],
  },
  he: {
    updated: "עודכן",
    title: "מדריכי שייט קשורים ואשכולות תכנון",
    intro: "השתמשו בעמודים המחוברים כדי להשוות תנאים, לתכנן מסלולים בטוחים יותר ולהמשיך מחיפושים רחבים לתשובות שייט ממוקדות.",
    schema: "אשכולות נושאי SEO של Marstick",
    clusters: [
      ["אשכול מזג אוויר", "חיזוק חיפושי מזג אוויר לשייט באמצעות חיבור רוח, גלים, בופור וגאות ושפל.", ["מדריך רוח", "מצב ים וגלים", "מדריך בופור", "מדריך גאות ושפל"]],
      ["אשכול מרינות", "תמיכה בכוונת חיפוש מרינות עם איתור, עמודי קרוב אליי ועמודי מדינות.", ["איתור מרינות", "מרינות קרוב אליי", "מרינות ביוון", "מרינות בקרואטיה"]],
      ["אשכול לימוד", "חיזוק מסלולי לימוד בטיחות, ניווט ותכנון הפלגה.", ["מרכז לימוד", "צ׳קליסט בטיחות", "מדריך ניווט", "תכנון הפלגה"]],
    ],
  },
  es: {
    updated: "Actualizado",
    title: "Guías de navegación relacionadas y grupos de planificación",
    intro: "Usa estas páginas conectadas para comparar condiciones, planificar rutas más seguras y pasar de búsquedas generales a respuestas náuticas específicas.",
    schema: "Grupos temáticos SEO de Marstick",
    clusters: [
      ["Grupo de meteorología", "Impulsa búsquedas de tiempo para navegar conectando viento, olas, Beaufort y mareas.", ["Guía de viento", "Estado del mar y olas", "Guía Beaufort", "Guía de mareas"]],
      ["Grupo de marinas", "Apoya la intención de descubrir marinas con buscador, páginas cercanas y países.", ["Buscador de marinas", "Marinas cerca de mí", "Marinas en Grecia", "Marinas en Croacia"]],
      ["Grupo de aprendizaje", "Refuerza rutas de aprendizaje sobre seguridad, navegación y planificación de travesías.", ["Centro de aprendizaje", "Checklist de seguridad", "Guía de navegación", "Planificación de travesía"]],
    ],
  },
  it: {
    updated: "Aggiornato",
    title: "Guide di vela correlate e cluster di pianificazione",
    intro: "Usa queste pagine collegate per confrontare condizioni, pianificare rotte più sicure e passare da ricerche ampie a risposte nautiche specifiche.",
    schema: "Cluster tematici SEO di Marstick",
    clusters: [
      ["Cluster meteo", "Rafforza le query meteo per la vela collegando vento, onde, Beaufort e maree.", ["Guida al vento", "Stato del mare e onde", "Guida Beaufort", "Guida alle maree"]],
      ["Cluster marine", "Supporta l’intento di ricerca marine con finder, pagine vicino a me e pagine paese.", ["Trova marine", "Marine vicino a me", "Marine in Grecia", "Marine in Croazia"]],
      ["Cluster apprendimento", "Rafforza percorsi su sicurezza, navigazione e pianificazione della traversata.", ["Centro apprendimento", "Checklist sicurezza", "Guida navigazione", "Pianificazione traversata"]],
    ],
  },
  fr: {
    updated: "Mis à jour",
    title: "Guides de navigation associés et clusters de planification",
    intro: "Utilisez ces pages reliées pour comparer les conditions, planifier des routes plus sûres et passer de recherches larges à des réponses nautiques précises.",
    schema: "Clusters thématiques SEO Marstick",
    clusters: [
      ["Cluster météo", "Renforce les requêtes météo voile en reliant vent, vagues, Beaufort et marées.", ["Guide du vent", "État de mer et vagues", "Guide Beaufort", "Guide des marées"]],
      ["Cluster marinas", "Soutient l’intention de recherche avec le finder, les pages près de moi et les pages pays.", ["Recherche de marinas", "Marinas près de moi", "Marinas en Grèce", "Marinas en Croatie"]],
      ["Cluster apprentissage", "Renforce les parcours sécurité, navigation et planification de passage.", ["Centre d’apprentissage", "Checklist sécurité", "Guide de navigation", "Planification de passage"]],
    ],
  },
  de: {
    updated: "Aktualisiert",
    title: "Verwandte Segelguides und Planungscluster",
    intro: "Nutzen Sie diese verbundenen Seiten, um Bedingungen zu vergleichen, sicherere Routen zu planen und von breiten Suchen zu konkreten Segelantworten zu gelangen.",
    schema: "Marstick SEO-Themencluster",
    clusters: [
      ["Wetter-Cluster", "Stärkt Segelwetter-Suchen durch Wind-, Wellen-, Beaufort- und Gezeiten-Guides.", ["Wind-Guide", "Seegang und Wellen", "Beaufort-Guide", "Gezeiten-Guide"]],
      ["Marina-Cluster", "Unterstützt Marina-Suchintention mit Finder, In-der-Nähe- und Länder-Seiten.", ["Marina-Finder", "Marinas in der Nähe", "Marinas in Griechenland", "Marinas in Kroatien"]],
      ["Lern-Cluster", "Stärkt Lernpfade zu Sicherheit, Navigation und Törnplanung.", ["Lernzentrum", "Sicherheitscheckliste", "Navigationsguide", "Törnplanung"]],
    ],
  },
  pt: {
    updated: "Atualizado",
    title: "Guias de navegação relacionados e clusters de planejamento",
    intro: "Use estas páginas conectadas para comparar condições, planejar rotas mais seguras e avançar de pesquisas amplas para respostas náuticas específicas.",
    schema: "Clusters de tópicos SEO da Marstick",
    clusters: [
      ["Cluster de meteorologia", "Fortalece buscas de clima para vela conectando vento, ondas, Beaufort e marés.", ["Guia de vento", "Estado do mar e ondas", "Guia Beaufort", "Guia de marés"]],
      ["Cluster de marinas", "Apoia a intenção de descoberta com localizador, páginas perto de mim e páginas por país.", ["Localizador de marinas", "Marinas perto de mim", "Marinas na Grécia", "Marinas na Croácia"]],
      ["Cluster de aprendizagem", "Fortalece trilhas de segurança, navegação e planejamento de passagem.", ["Centro de aprendizagem", "Checklist de segurança", "Guia de navegação", "Planejamento de passagem"]],
    ],
  },
  ar: {
    updated: "تم التحديث",
    title: "أدلة إبحار مرتبطة ومجموعات تخطيط",
    intro: "استخدم هذه الصفحات المترابطة لمقارنة الظروف، وتخطيط مسارات أكثر أمانًا، والانتقال من البحث العام إلى إجابات بحرية محددة.",
    schema: "مجموعات مواضيع SEO في Marstick",
    clusters: [
      ["مجموعة الطقس", "تعزيز طلبات طقس الإبحار عبر ربط الرياح والأمواج وبوفورت والمد والجزر.", ["دليل الرياح", "حالة البحر والأمواج", "دليل بوفورت", "دليل المد والجزر"]],
      ["مجموعة المراسي", "دعم نية البحث عن المراسي عبر أداة البحث وصفحات القرب وصفحات الدول.", ["البحث عن المراسي", "مراسي قريبة مني", "مراسي اليونان", "مراسي كرواتيا"]],
      ["مجموعة التعلم", "تقوية مسارات تعلم السلامة والملاحة وتخطيط الرحلات.", ["مركز التعلم", "قائمة السلامة", "دليل الملاحة", "تخطيط الرحلة"]],
    ],
  },
  el: {
    updated: "Ενημερώθηκε",
    title: "Σχετικοί οδηγοί ιστιοπλοΐας και ομάδες σχεδιασμού",
    intro: "Χρησιμοποιήστε αυτές τις συνδεδεμένες σελίδες για σύγκριση συνθηκών, ασφαλέστερες διαδρομές και μετάβαση από γενικές αναζητήσεις σε συγκεκριμένες ναυτικές απαντήσεις.",
    schema: "Θεματικά clusters SEO Marstick",
    clusters: [
      ["Cluster καιρού", "Ενισχύει αναζητήσεις καιρού ιστιοπλοΐας συνδέοντας άνεμο, κύματα, Beaufort και παλίρροιες.", ["Οδηγός ανέμου", "Κατάσταση θάλασσας και κύματα", "Οδηγός Beaufort", "Οδηγός παλίρροιας"]],
      ["Cluster μαρινών", "Υποστηρίζει αναζητήσεις μαρινών με finder, σελίδες κοντά μου και σελίδες χωρών.", ["Εύρεση μαρινών", "Μαρίνες κοντά μου", "Μαρίνες Ελλάδας", "Μαρίνες Κροατίας"]],
      ["Cluster μάθησης", "Ενισχύει διαδρομές μάθησης για ασφάλεια, πλοήγηση και σχεδιασμό πλου.", ["Κέντρο μάθησης", "Λίστα ασφάλειας", "Οδηγός πλοήγησης", "Σχεδιασμός πλου"]],
    ],
  },
  ru: {
    updated: "Обновлено",
    title: "Связанные руководства по яхтингу и кластеры планирования",
    intro: "Используйте связанные страницы, чтобы сравнивать условия, планировать более безопасные маршруты и переходить от широких запросов к точным морским ответам.",
    schema: "SEO-тематические кластеры Marstick",
    clusters: [
      ["Кластер погоды", "Усиливает запросы по погоде для яхтинга через ветер, волны, шкалу Бофорта и приливы.", ["Гид по ветру", "Состояние моря и волны", "Гид по Бофорту", "Гид по приливам"]],
      ["Кластер марин", "Поддерживает поиск марин через finder, страницы рядом со мной и страницы стран.", ["Поиск марин", "Марины рядом", "Марины Греции", "Марины Хорватии"]],
      ["Кластер обучения", "Укрепляет учебные пути по безопасности, навигации и планированию перехода.", ["Учебный центр", "Чек-лист безопасности", "Гид по навигации", "Планирование перехода"]],
    ],
  },
  tr: {
    updated: "Güncellendi",
    title: "İlgili yelken rehberleri ve planlama kümeleri",
    intro: "Koşulları karşılaştırmak, daha güvenli rotalar planlamak ve geniş aramalardan özel denizcilik yanıtlarına geçmek için bu bağlantılı sayfaları kullanın.",
    schema: "Marstick SEO konu kümeleri",
    clusters: [
      ["Hava durumu kümesi", "Rüzgar, dalga, Beaufort ve gelgit rehberlerini bağlayarak yelken hava durumu sorgularını güçlendirir.", ["Rüzgar rehberi", "Deniz durumu ve dalgalar", "Beaufort rehberi", "Gelgit rehberi"]],
      ["Marina kümesi", "Bulucu, yakınımdaki sayfalar ve ülke sayfalarıyla marina keşif niyetini destekler.", ["Marina bulucu", "Yakınımdaki marinalar", "Yunanistan marinaları", "Hırvatistan marinaları"]],
      ["Öğrenme kümesi", "Güvenlik, navigasyon ve geçiş planlama öğrenme yollarını güçlendirir.", ["Öğrenme merkezi", "Güvenlik kontrol listesi", "Navigasyon rehberi", "Geçiş planlama"]],
    ],
  },
  zh: {
    updated: "已更新",
    title: "相关航海指南与规划主题集群",
    intro: "使用这些相互连接的页面来比较条件、规划更安全的航线，并从宽泛搜索进入更具体的航海答案。",
    schema: "Marstick SEO 主题集群",
    clusters: [
      ["天气主题集群", "通过连接风、浪、蒲福风级和潮汐指南，提升航海天气搜索覆盖。", ["风力指南", "海况与海浪", "蒲福风级指南", "潮汐指南"]],
      ["码头主题集群", "通过查找器、附近页面和国家页面支持码头发现搜索意图。", ["码头查找器", "附近码头", "希腊码头", "克罗地亚码头"]],
      ["学习主题集群", "加强安全、导航和航程规划学习路径。", ["学习中心", "安全检查清单", "导航指南", "航程规划"]],
    ],
  },
};

function clusterLinks(lang) {
  const weatherBase = getLocalizedPath("weather", lang);
  const marinasBase = getLocalizedPath("marinas", lang);
  const learningBase = getLocalizedPath("learningCenter", lang);
  return [
    [`${weatherBase}/wind-guide`, `${weatherBase}/sea-state-guide`, `${weatherBase}/beaufort-guide`, `${weatherBase}/tide-guide`],
    [marinasBase, `/${lang}/marinas-near-me`, `${marinasBase}/greece`, `${marinasBase}/croatia`],
    [learningBase, `/${lang}/safety/pre-departure-checklist`, getLocalizedPath("navigation", lang), `${learningBase}/passage-planning`],
  ];
}

export default function PublicSeoTopicClusters({ lang = "en" }) {
  const copy = copyByLang[lang];

  if (!copy) return null;

  const paths = clusterLinks(lang);
  const updated = new Intl.DateTimeFormat(lang, { month: "short", day: "numeric", year: "numeric" }).format(new Date());
  const schema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: copy.schema,
    itemListElement: copy.clusters.flatMap((cluster, clusterIndex) => cluster[2].map((name, linkIndex) => ({
      "@type": "ListItem",
      position: clusterIndex * 4 + linkIndex + 1,
      name,
      url: `https://marstick.com${paths[clusterIndex][linkIndex]}`,
    }))),
  };

  return (
    <section dir={rtlLangs.has(lang) ? "rtl" : "ltr"} className="mt-8 rounded-[2rem] border border-primary/15 bg-card p-5 shadow-xl md:p-7" aria-labelledby="seo-topic-clusters-title">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
      <p className="text-xs font-black uppercase tracking-[0.24em] text-primary">{copy.updated} {updated}</p>
      <h2 id="seo-topic-clusters-title" className="mt-2 font-heading text-2xl font-black text-foreground">{copy.title}</h2>
      <p className="mt-2 max-w-3xl text-sm leading-7 text-muted-foreground">{copy.intro}</p>
      <div className="mt-5 grid gap-4 lg:grid-cols-3">
        {copy.clusters.map(([title, intro, labels], clusterIndex) => {
          const Icon = [CloudSun, Anchor, BookOpen][clusterIndex];
          return (
            <article key={title} className="rounded-3xl border border-border bg-background/70 p-4">
              <h3 className="flex items-center gap-2 font-black"><Icon className="h-5 w-5 text-primary" /> {title}</h3>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">{intro}</p>
              <div className="mt-4 grid gap-2">
                {labels.map((label, linkIndex) => <Link key={label} to={paths[clusterIndex][linkIndex]} className="rounded-2xl border border-border bg-card px-4 py-3 text-sm font-black hover:border-primary/60 hover:text-primary">{label}</Link>)}
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}